import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import {SpreadsheetLocaleSelector} from 'bulk-import/panes/SpreadsheetLocaleSelector';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {DEFAULT_LANGUAGE_TAG} from 'i18n/LanguageTagConstants';
import FieldDefinitionInputOrDisplay from 'registry/field/FieldDefinitionInputOrDisplay';
import {
  PopulateFromAnotherFieldQuery,
  PopulateFromAnotherFieldQuery_schema_BatchSchemaNode_allFieldsNodes_SchemaFieldNode as SchemaFieldGQL,
} from 'schemas/fields/field-migration/__generated/PopulateFromAnotherFieldQuery';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import * as TagSchemaUtil from 'tag-schema/util/TagSchemaUtil';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import HorizontalThrobber, {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';
import {SettingsLabel} from 'ui-library/SettingsField';

import SourceFieldDropdownInput from '../SourceFieldDropdownInput';
import {shouldShowLocaleSelector} from './FieldMigrationCreationModal';
import {SimpleTable} from './SimpleTable';

export interface FieldToFieldMigrationSettings {
  sourceFieldId: string;
  locale?: string;
}

interface BasePopulateFromAnotherFieldModalProps {
  schema: Schema;
  schemaNumItems: number | null;
  shouldShowIsParentLink: boolean;
  shouldShowIsIntegration: boolean;
  onRequestSubmit: (settings: FieldToFieldMigrationSettings) => void;
  onRequestClose: () => void;
  schemaFieldSelectors: Array<SchemaFieldSelector> | null;
}

type QueryingPopulateFromAnotherFieldModalProps = BasePopulateFromAnotherFieldModalProps & {
  destinationField: SchemaField;
};

type SchemaFieldForFieldMigration = SchemaField & {
  numFieldValues: number;
};

type PopulateFromAnotherFieldModalProps = BasePopulateFromAnotherFieldModalProps & {
  destinationField: SchemaFieldForFieldMigration;
};

export function PopulateFromAnotherFieldModal(props: PopulateFromAnotherFieldModalProps): JSX.Element {
  const sourceFieldOptions = props.schema.allFields.filter(
    (field) => field.apiId !== props.destinationField.apiId && field.archiveRecord == null
  );
  const [currentSourceField, setCurrentSourceField] = useState<SchemaField>(sourceFieldOptions[0]);
  const shouldShowIsParentLink =
    props.shouldShowIsParentLink && TagSchemaUtil.isBioentityLinkFieldType(currentSourceField.fieldType);
  const numItemsElement = props.schemaNumItems ? `${props.schemaNumItems}` : <HorizontalThrobber />;
  const [locale, setLocale] = useState(DEFAULT_LANGUAGE_TAG.code);

  const handleSelectSourceField = (field: SchemaField): void => {
    setCurrentSourceField(field);
  };

  const maybeRenderNonEmptyFieldErrorBanner = (): JSX.Element | null =>
    props.destinationField.numFieldValues > 0 ? (
      <div className="u-small-spacing-top">
        <InlineBanner
          text="This field cannot be populated, as it already contains data"
          supportingInfo="If you wish to populate this field, first remove data from all items where it has a value."
          bannerType={InlineBannerType.ERROR}
        />
      </div>
    ) : null;

  const renderModalConfig = (): JSX.Element => (
    <>
      <div className={css(styles.dropdownSection)}>
        <SourceFieldDropdownInput
          destinationField={props.destinationField}
          onSelectField={handleSelectSourceField}
          sourceFieldOptions={sourceFieldOptions}
          currentField={currentSourceField}
        />
      </div>
      <div className={css(styles.checkboxRow)}>
        <div className={css(styles.checkboxCol)}>
          <SettingsLabel label="Required" isRequired={false} withBottomMargin={true} />
          <div className={css(styles.check)}>
            {currentSourceField.isRequired && (
              <BenchlingIcon icon="check-circle" className={css(styles.checkedIcon)} />
            )}
          </div>
        </div>
        <div className={css(styles.checkboxCol)}>
          <SettingsLabel label="Multi-select" isRequired={false} withBottomMargin={true} />
          <div className={css(styles.check)}>
            {currentSourceField.isMulti && (
              <BenchlingIcon icon="check-circle" className={css(styles.checkedIcon)} />
            )}
          </div>
        </div>
        {shouldShowIsParentLink && (
          <div className={css(styles.checkboxCol)}>
            <SettingsLabel label="Parent link" isRequired={false} withBottomMargin={true} />
            <div className={css(styles.check)}>
              {currentSourceField.isParentLink && (
                <BenchlingIcon icon="check-circle" className={css(styles.checkedIcon)} />
              )}
            </div>
          </div>
        )}
        {props.shouldShowIsIntegration && (
          <div className={css(styles.checkboxCol)}>
            <SettingsLabel label="Integration" isRequired={false} withBottomMargin={true} />
            <div className={css(styles.check)}>
              {currentSourceField.isIntegration && (
                <BenchlingIcon icon="check-circle" className={css(styles.checkedIcon)} />
              )}
            </div>
          </div>
        )}
        <div className={css(styles.definitionCol)}>
          <SettingsLabel label="Definition (type)" isRequired={false} withBottomMargin={true} />
          <FieldDefinitionInputOrDisplay
            isFieldBeingCreated={false}
            availableFieldOptions={[]}
            schemaFieldSelectors={props.schemaFieldSelectors ? props.schemaFieldSelectors : []}
            currentFieldState={currentSourceField}
            inEditMode={false}
            onSelectFieldOption={(_) => {}}
          />
        </div>
      </div>
    </>
  );

  return (
    <Modal
      title="Populate from another field"
      onRequestSubmit={() => {
        props.onRequestSubmit({sourceFieldId: currentSourceField.apiId, locale});
      }}
      onRequestClose={props.onRequestClose}
      scrollable={true}
      mediumLarge={true}
      className="modal-text-only"
      buttons={[
        {text: 'Cancel', cancel: true},
        {
          text: 'Begin Migration',
          submit: true,
          style: ButtonStyle.PRIMARY,
          disabled: props.destinationField.numFieldValues > 0,
        },
      ]}
    >
      {maybeRenderNonEmptyFieldErrorBanner() ?? renderModalConfig()}
      <div className={css(styles.definitionAndDescriptionSection)}>
        <div className={css(styles.descriptionCol)}>
          <SettingsLabel label="Description" isRequired={false} withBottomMargin={true} />
          <div>{currentSourceField.description}</div>
        </div>
      </div>
      {shouldShowLocaleSelector(currentSourceField, props.destinationField) && (
        <div className={css(styles.localeSelector)}>
          <SpreadsheetLocaleSelector languageTag={locale} onChange={setLocale} />
        </div>
      )}
      <div className={css(styles.tableContainer)}>
        <SimpleTable
          columns={['Item Count', 'Items with data in this field']}
          values={[[numItemsElement, `${props.destinationField.numFieldValues}`]]}
        />
      </div>
    </Modal>
  );
}

export const POPULATE_FROM_ANOTHER_FIELD_QUERY = gql`
  # schema.allFieldsNodes does not query id
  # eslint-disable benchlint/id-field-required
  query PopulateFromAnotherFieldQuery($schemaId: StrictString!, $fieldId: StrictString!)
  @skipAudit
  @engComponent(name: SCHEMAS) {
    schema: schema(id: $schemaId) {
      ... on SchemaInterface {
        id
        allFieldsNodes(ids: [$fieldId]) {
          apiId
          numFieldValues
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function QueryingPopulateFromAnotherFieldModal(
  props: QueryingPopulateFromAnotherFieldModalProps
): JSX.Element {
  return (
    <BenchlingQuery<PopulateFromAnotherFieldQuery>
      query={POPULATE_FROM_ANOTHER_FIELD_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{schemaId: props.schema.id, fieldId: props.destinationField.apiId}}
    >
      {(data) => {
        assert(!isInaccessible(data.schema), 'Expected schema to be accessible');
        const destinationFieldGQL = data.schema.allFieldsNodes.find(
          (field: SchemaFieldGQL) => field.apiId === props.destinationField.apiId
        );
        assert(
          destinationFieldGQL != null,
          `Did not find field with api id ${props.destinationField.apiId} on schema ${props.schema.id}`
        );
        const destinationField: SchemaFieldForFieldMigration = {
          ...props.destinationField,
          numFieldValues: destinationFieldGQL.numFieldValues,
        };
        return <PopulateFromAnotherFieldModal {...props} destinationField={destinationField} />;
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  dropdownSection: {
    display: 'flex',
    width: '100%',
    flexDirection: 'row',
  },
  checkboxRow: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: '8px',
  },
  checkboxCol: {
    display: 'flex',
    flexDirection: 'column',
    marginRight: '16px',
    minHeight: '40px',
    alignItems: 'center',
  },
  check: {
    marginTop: '4px',
    align: 'center',
  },
  checkedIcon: {
    color: Colors.GREEN,
    align: 'left',
  },
  definitionAndDescriptionSection: {
    display: 'flex',
    marginTop: '8px',
    flexDirection: 'row',
  },
  definitionCol: {
    display: 'flex',
    flexDirection: 'column',
    marginRight: '16px',
    align: 'left',
  },
  tableContainer: {
    marginTop: '16px',
  },
  descriptionCol: {
    display: 'flex',
    flexDirection: 'column',
  },
  localeSelector: {
    marginTop: '16px',
  },
});
